import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import useMenuItems from '../../hooks/useMenuItems';
import './TableMenu.css';

const TableMenu = () => {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const { menuItems, loading } = useMenuItems();
  const [table, setTable] = useState(null);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [vegOnly, setVegOnly] = useState(false);

  useEffect(() => {
    fetchTableInfo();
    loadCart();
  }, [tableId]);

  const fetchTableInfo = async () => {
    try {
      const tableDoc = await getDoc(doc(db, 'tables', tableId));
      if (tableDoc.exists()) {
        setTable({ id: tableDoc.id, ...tableDoc.data() });
      } else {
        alert('Table not found');
        navigate('/qr');
      }
    } catch (error) {
      console.error('Error fetching table:', error);
    }
  };

  const loadCart = () => {
    const savedCart = localStorage.getItem(`qr-cart-${tableId}`);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const saveCart = (updatedCart) => {
    localStorage.setItem(`qr-cart-${tableId}`, JSON.stringify(updatedCart));
    setCart(updatedCart);
  };

  const addToCart = (item) => {
    const existingItem = cart.find(i => i.id === item.id);
    let updatedCart;

    if (existingItem) {
      updatedCart = cart.map(i => 
        i.id === item.id 
          ? { ...i, quantity: i.quantity + 1 }
          : i
      );
    } else {
      updatedCart = [...cart, { ...item, quantity: 1 }];
    }

    saveCart(updatedCart);
  };

  const removeFromCart = (itemId) => {
    const existingItem = cart.find(i => i.id === itemId);
    let updatedCart;

    if (existingItem.quantity > 1) {
      updatedCart = cart.map(i => 
        i.id === itemId 
          ? { ...i, quantity: i.quantity - 1 }
          : i
      );
    } else {
      updatedCart = cart.filter(i => i.id !== itemId);
    }

    saveCart(updatedCart);
  };

  const getCartQuantity = (itemId) => {
    const item = cart.find(i => i.id === itemId);
    return item ? item.quantity : 0;
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const categories = ['All', ...new Set(menuItems.map(item => item.category))];

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesVeg = !vegOnly || item.isVeg;
    const isAvailable = item.available !== false;

    return matchesCategory && matchesSearch && matchesVeg && isAvailable;
  });

  if (loading || !table) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Loading menu...</p>
      </div>
    );
  }

  return (
    <div className="table-menu-page">
      <div className="menu-header">
        <div className="table-info">
          <button className="back-btn" onClick={() => navigate('/qr')}>←</button>
          <div>
            <h2>Table {table.tableNumber}</h2>
            <p>{table.floor} • {table.section}</p>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className={`veg-toggle ${vegOnly ? 'active' : ''}`}
            onClick={() => setVegOnly(!vegOnly)}
          >
            🟢 Veg Only
          </button>
        </div>
      </div>

      <div className="search-section">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="Search dishes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
      </div>

      <div className="category-tabs">
        {categories.map(category => (
          <button
            key={category}
            className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="menu-items-grid">
        {filteredItems.length === 0 ? (
          <div className="empty-state">
            <p>No items found</p>
          </div>
        ) : (
          filteredItems.map(item => {
            const quantity = getCartQuantity(item.id);
            
            return (
              <div key={item.id} className="menu-item-card">
                {item.imageUrl && (
                  <div className="item-image-wrapper">
                    <img src={item.imageUrl} alt={item.name} className="item-image" />
                    {item.isVeg !== undefined && (
                      <span className={`veg-indicator ${item.isVeg ? 'veg' : 'non-veg'}`}>
                        {item.isVeg ? '🟢' : '🔴'}
                      </span>
                    )}
                  </div>
                )}
                
                <div className="item-details">
                  <h3>{item.name}</h3>
                  {item.description && <p className="item-description">{item.description}</p>}
                  <div className="item-footer">
                    <span className="item-price">₹{item.price}</span>
                    
                    {quantity === 0 ? (
                      <button 
                        className="btn-add"
                        onClick={() => addToCart(item)}
                      >
                        Add +
                      </button>
                    ) : (
                      <div className="quantity-control">
                        <button 
                          className="qty-btn"
                          onClick={() => removeFromCart(item.id)}
                        >
                          −
                        </button>
                        <span className="qty-display">{quantity}</span>
                        <button 
                          className="qty-btn"
                          onClick={() => addToCart(item)}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {cart.length > 0 && (
        <div className="cart-footer">
          <div className="cart-summary">
            <div>
              <p className="cart-items-count">{cart.length} items</p>
              <p className="cart-total">₹{getCartTotal()}</p>
            </div>
            <button 
              className="btn-view-cart"
              onClick={() => navigate(`/qr/cart/${tableId}`)}
            >
              View Cart →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableMenu;
